// src/hooks/useBlogPost.ts
import { useState, useEffect } from 'react';
import { BlogPost, BlogPostWithContent } from '../types/blog';
import { extractTagsFromHTML } from '../utils/tagExtraction';

interface SEOData {
  tags: string[];
  headlineImage?: string;
  title?: string;
  description?: string;
}

interface UseBlogPostReturn {
  post: BlogPostWithContent | null;
  loading: boolean;
  error: string | null;
  seoData: SEOData | null;
}

const extractSEOFromHTML = async (htmlContent: string): Promise<SEOData> => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  
  // Use the enhanced tag extraction utility
  const tags = await extractTagsFromHTML(htmlContent);
  
  // Extract headline image
  const headlineImageMeta = doc.querySelector('meta[name="headline_image_date"]');
  const headlineImage = headlineImageMeta?.getAttribute('content') || '';
  
  // Extract title from the document
  const titleElement = doc.querySelector('title');
  const title = titleElement?.textContent || '';
  
  // Extract first few sentences for description
  const firstParagraph = doc.querySelector('.market-preview');
  const description = firstParagraph?.textContent?.substring(0, 160) || '';
  
  return {
    tags,
    headlineImage,
    title,
    description
  };
};

export const useBlogPost = (blogPost: BlogPost): UseBlogPostReturn => {
  const [post, setPost] = useState<BlogPostWithContent | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [seoData, setSeoData] = useState<SEOData | null>(null);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(blogPost.s3Url);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const content = await response.text();
        const extractedSEO = await extractSEOFromHTML(content);
        
        setPost({ ...blogPost, content, extractedTags: extractedSEO.tags });
        setSeoData(extractedSEO);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch blog post');
        setPost(null);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [blogPost.s3Url, blogPost.id]);

  return { post, loading, error, seoData };
};
